import { Box } from '@hivebrite/design-system/Box';

import LoginCard from 'hb-builder/components/common/LoginCard';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { defaultTabs } from 'hb-builder/constants/tabs';
import {
  useComponentContext,
  useComponentsContext,
  useBuilderConfigContext,
  useRendererContext,
} from 'hb-builder/hooks';
import type { BuilderComponent } from 'hb-builder/types';
import { useGetUserInUserBanner } from 'hb-react/backoffice/requests/dynamicComponents/useGetUserInUserBanner';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { SectionItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/sectionItemAttributes';
import { APP_TYPE } from 'hb-react/types/appType';

import UserBannerLayout from './UserBannerLayout';
import { getUserBannerItemChildren } from './utils/getUserBannerItemChildren';

const CONTENT_TYPE = 'user';

const UserBanner: BuilderComponent<SectionItemAttributes> = () => <div />; // never used

const RenderBuilder = () => {
  const { item } = useComponentContext();
  const { components } = useComponentsContext();
  const {
    hivebrite: { networkFont },
  } = useBuilderConfigContext();

  const attributes = item?.attributes;
  const { Section } = components;

  return (
    <Section.RenderBuilder attributes={attributes}>
      <Box
        data-testid="user-banner-builder"
        display="block"
        fontFamily={networkFont}
      >
        <UserBannerLayout
          app={APP_TYPE.BACKOFFICE}
          item={item as DocumentItem<SectionItemAttributes>}
        />
      </Box>
    </Section.RenderBuilder>
  );
};

const Render = ({ item }: RenderProps<DocumentItem<SectionItemAttributes>>) => {
  const { bodyWidth, isMobilePreview, isPublic, workspaceConfig } =
    useRendererContext();
  const { components } = useComponentsContext();
  const { Section } = components;
  const { data: userData, isLoading } = useGetUserInUserBanner({
    enabled: !isPublic,
  });

  if (isPublic) {
    return <LoginCard componentName={CONTENT_TYPE} iconName="users" />;
  }

  return (
    <Section.Render
      bodyWidth={bodyWidth}
      item={item}
      isMobilePreview={isMobilePreview}
      workspaceConfig={workspaceConfig}
    >
      <UserBannerLayout isLoading={isLoading} item={item} user={userData} />
    </Section.Render>
  );
};

UserBanner.componentFamily = 'Hero';
UserBanner.componentName = 'UserBanner';
UserBanner.componentType = 'section';

UserBanner.componentLabel = __('User Banner');
UserBanner.componentCategory = 'block';

UserBanner.canBeDoubleClicked = true;
UserBanner.canBeDragged = true;
UserBanner.canBeListed = true;
UserBanner.canBeSelected = true;

UserBanner.item = (props) => {
  const { bodyWidth, components, features, featureFlags, theme } = props;
  const { Section } = components;

  return Section.item({
    ...props,
    children: getUserBannerItemChildren({
      components,
      featureFlags,
      features,
      theme,
    }),
    components,
    features: features
      ? {
          ...features,
          section: {
            ...features?.section,
            category: UserBanner.componentCategory,
            containerBackgroundColor: 'white',
            containerBoxShadow: 'rgb(0 0 0 / 10%) 0px 1px 6px 0px',
            fullWidth: true,
            name: UserBanner.componentName,
            sectionWidth: bodyWidth,
            type: UserBanner.componentName,
          },
        }
      : undefined,
  });
};

UserBanner.toolbar = ['edit', 'drag', 'duplicate', 'delete'];

UserBanner.SettingsTabs = defaultTabs;

UserBanner.RenderBuilder = RenderBuilder;
UserBanner.Render = Render;

export default UserBanner;
